import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { BLOGS } from "../data/blogsData";

/* ── helpers ── */
const ALL = "All";
const categories = [ALL, ...Array.from(new Set(BLOGS.map((b) => b.category)))];

/* ── Blog Card ── */
function BlogCard({ blog }) {
  return (
    <Link
      to={`/blog/${blog.slug}`}
      className="blog-card"
      aria-label={`Read article: ${blog.title}`}
    >
      <div className="blog-card__img-wrap">
        <img
          src={blog.image}
          alt={blog.imageAlt}
          className="blog-card__img"
          loading="lazy"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentElement.classList.add("blog-card__img-wrap--fallback");
          }}
        />
        <span className="blog-card__cat">{blog.category}</span>
      </div>
      <div className="blog-card__body">
        <div className="blog-card__meta">
          <time dateTime={blog.datePublished}>{blog.dateDisplay}</time>
          <span className="blog-card__dot">·</span>
          <span>{blog.readTime}</span>
        </div>
        <h2 className="blog-card__title">{blog.title}</h2>
        <p className="blog-card__excerpt">{blog.excerpt}</p>
        <span className="blog-card__cta">
          Read Article
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

/* ── Page ── */
export default function Blogs() {
  const [activeCategory, setActiveCategory] = useState(ALL);

  const filtered = useMemo(
    () =>
      activeCategory === ALL
        ? BLOGS
        : BLOGS.filter((b) => b.category === activeCategory),
    [activeCategory]
  );

  const canonicalUrl = "https://www.novacapitaldxb.com/blog";

  return (
    <>
      {/* ── Full SEO ── */}
      <Helmet>
        <title>Insights & Market Reports | Nova Capital Real Estate Dubai</title>
        <meta
          name="description"
          content="Expert analysis, Dubai property market reports, investment guides and real estate insights from Nova Capital — your zero-commission advisor in Dubai."
        />
        <meta
          name="keywords"
          content="Dubai real estate blog, Dubai property market 2026, Nova Capital insights, Dubai investment guides, off-plan Dubai, property market analysis"
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Nova Capital Real Estate" />
        <meta property="og:title" content="Insights & Market Reports | Nova Capital Real Estate Dubai" />
        <meta
          property="og:description"
          content="Expert analysis, Dubai property market reports, investment guides and real estate insights from Nova Capital — your zero-commission advisor in Dubai."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.novacapitaldxb.com/images/blog-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Insights & Market Reports | Nova Capital Real Estate Dubai" />
        <meta
          name="twitter:description"
          content="Expert analysis, Dubai property market reports, investment guides and real estate insights from Nova Capital."
        />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Nova Capital Real Estate Insights",
          "url": canonicalUrl,
          "description": "Expert analysis, Dubai property market reports and real estate insights from Nova Capital.",
          "publisher": {
            "@type": "Organization",
            "name": "Nova Capital Real Estate",
            "url": "https://www.novacapitaldxb.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.novacapitaldxb.com/logo.png"
            }
          },
          "blogPost": BLOGS.map((b) => ({
            "@type": "BlogPosting",
            "headline": b.title,
            "description": b.excerpt,
            "url": `https://www.novacapitaldxb.com/blog/${b.slug}`,
            "datePublished": b.datePublished,
            "image": b.image,
            "author": {
              "@type": "Organization",
              "name": b.author
            }
          }))
        })}</script>
      </Helmet>

      <Navbar />

      <main className="blogs-page">
        {/* ── Hero ── */}
        <section className="blogs-hero">
          <div className="blogs-hero__overlay" />
          <div className="blogs-hero__content">
            <span className="blogs-hero__eyebrow">Nova Capital</span>
            <h1 className="blogs-hero__heading">Insights &amp; Market Reports</h1>
            <p className="blogs-hero__sub">
              Data-driven analysis of Dubai's property market — zero spin, zero commission agenda.
            </p>
          </div>
        </section>

        {/* ── Category Filter ── */}
        <section className="blogs-filter-wrap">
          <div className="blogs-filter">
            {categories.map((cat) => (
              <button
                key={cat}
                id={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`blogs-filter__btn${activeCategory === cat ? " blogs-filter__btn--active" : ""}`}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={activeCategory === cat}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* ── Grid ── */}
        <section className="blogs-grid-wrap">
          {filtered.length === 0 ? (
            <p className="blogs-empty">No articles in this category yet — check back soon.</p>
          ) : (
            <div className="blogs-grid">
              {filtered.map((blog) => (
                <BlogCard key={blog.slug} blog={blog} />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />

      <style>{`
        /* ═══════════════ BLOGS PAGE STYLES ═══════════════ */
        :root {
          --nc-gold: #C9A84C;
          --nc-gold-light: #b78800;
          // --nc-dark: #0F1117;
          // --nc-charcoal: #1C1F28;
          // --nc-mid: #2B2F3A;
          // --nc-text: #E8E6E1;
          // --nc-muted: #9A97A0;
          // --nc-border: rgba(201,168,76,0.18);
          --nc-dark: #F7F9FC;
            --nc-charcoal: #FFFFFF;
            --nc-mid: #EBF0F6;
            --nc-text: #1C1F28;
            --nc-muted: rgba(100, 116, 139, 1);
            --nc-border: rgba(201, 168, 76, 0.3);
          --nc-radius: 8px;
          --nc-font-display: 'Georgia', 'Times New Roman', serif;
        }

        .blogs-page {
          background: var(--nc-dark);
          min-height: 100vh;
        }

        /* ── Hero ── */
        .blogs-hero {
          position: relative;
          min-height: 380px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: var(--nc-charcoal);
          // background: linear-gradient(135deg, #0a0d14 0%, #1a1f2e 50%, #0f1117 100%);
          overflow: hidden;
          padding: 100px 24px 60px;
        }
        .blogs-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          // background: radial-gradient(ellipse 80% 60% at 50% 40%, rgba(201,168,76,0.12) 0%, transparent 70%);
          pointer-events: none;
        }
        .blogs-hero::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 80px;
          // background: linear-gradient(to bottom, transparent, var(--nc-dark));
        }
        .blogs-hero__overlay {
          position: absolute;
          inset: 0;}
        .blogs-hero__content {
          position: relative;
          z-index: 2;
          max-width: 700px;
        }
        .blogs-hero__eyebrow {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--nc-gold);
          border: 1px solid rgba(201,168,76,0.4);
          border-radius: 2px;
          padding: 4px 14px;
          margin-bottom: 20px;
        }
        .blogs-hero__heading {
          font-family: var(--nc-font-display);
          font-size: clamp(28px, 5vw, 52px);
          font-weight: 400;
          color: var(--nc-text);
          line-height: 1.18;
          margin-bottom: 18px;
        }
        .blogs-hero__sub {
          font-size: 16px;
          color: var(--nc-muted);
          line-height: 1.65;
          max-width: 540px;
          margin: 0 auto;
        }

        /* ── Filter ── */
        .blogs-filter-wrap {
          padding: 28px 24px 0;
          display: flex;
          justify-content: center;
        }
        .blogs-filter {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .blogs-filter__btn {
          background: transparent;
          border: 1px solid rgba(201,168,76,0.3);
          color: var(--nc-muted);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.06em;
          padding: 8px 20px;
          border-radius: 40px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .blogs-filter__btn:hover {
          border-color: var(--nc-gold);
          color: var(--nc-gold);
        }
        .blogs-filter__btn--active {
          background: var(--nc-gold);
          border-color: var(--nc-gold);
          color: #0F1117 !important;
        }

        /* ── Grid ── */
        .blogs-grid-wrap {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 24px 80px;
        }
        .blogs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 28px;
        }
        .blogs-empty {
          text-align: center;
          color: var(--nc-muted);
          font-size: 16px;
          padding: 60px 0;
        }

        /* ── Card ── */
        .blog-card {
          background: var(--nc-charcoal);
          border: 1px solid var(--nc-border);
          border-radius: var(--nc-radius);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          text-decoration: none;
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .blog-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(201,168,76,0.3);
          border-color: rgba(201,168,76,0.4);
        }

        /* Card image */
        .blog-card__img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 16/9;
          background: var(--nc-mid);
          overflow: hidden;
        }
        .blog-card__img-wrap--fallback {
          background: linear-gradient(135deg, #1a1f2e, #2b2f3a);
        }
        .blog-card__img-wrap--fallback::after {
          content: 'Nova Capital';
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--nc-gold);
          font-family: var(--nc-font-display);
          font-size: 22px;
          letter-spacing: 0.08em;
          opacity: 0.4;
        }
        .blog-card__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.4s ease;
        }
        .blog-card:hover .blog-card__img {
          transform: scale(1.04);
        }
        .blog-card__cat {
          position: absolute;
          top: 14px;
          left: 14px;
          background: var(--nc-dark);
          color: var(--nc-gold);
          border: 1px solid var(--nc-gold);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 3px 10px;
          border-radius: 2px;
        }

        /* Card body */
        .blog-card__body {
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          flex: 1;
        }
        .blog-card__meta {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--nc-muted);
        }
        .blog-card__dot { color: rgba(201,168,76,0.4); }
        .blog-card__title {
          font-family: var(--nc-font-display);
          font-size: 18px;
          font-weight: 400;
          color: var(--nc-text);
          line-height: 1.35;
          margin: 0;
        }
        .blog-card:hover .blog-card__title {
          color: var(--nc-gold-light);
        }
        .blog-card__excerpt {
          font-size: 14px;
          color: var(--nc-muted);
          line-height: 1.65;
          margin: 0;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blog-card__cta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.05em;
          color: var(--nc-gold);
          text-transform: uppercase;
          transition: gap 0.2s ease;
        }
        .blog-card:hover .blog-card__cta {
          gap: 10px;
        }

        /* ── Responsive ── */
        @media (max-width: 600px) {
          .blogs-grid {
            grid-template-columns: 1fr;
          }
          .blogs-hero {
            padding: 90px 18px 50px;
            min-height: 300px;
          }
        }
      `}</style>
    </>
  );
}
